/**
 * Site-wide banner shown when no OpenAI key is configured.
 *
 * Two variants:
 *   - admins: actionable link to /admin/settings
 *   - users: explanatory; "ask your admin"
 *
 * The flag is read from the unauthenticated /api/v1/health/openai endpoint so
 * the banner can render even before the user is logged in (though we only
 * mount it for authenticated users below).
 */
import type { OpenAIHealthFlag } from '@peerless/shared';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Settings as SettingsIcon } from 'lucide-react';
import { Link } from 'react-router-dom';

import { useAuth } from '../../hooks/useAuth';
import { apiRequest } from '../../lib/api-client';
import { queryKeys } from '../../lib/query-keys';


export function OpenAINotConfiguredBanner(): JSX.Element | null {
  const { user } = useAuth();
  const { data } = useQuery({
    queryKey: queryKeys.settings.openaiHealth(),
    queryFn: () =>
      apiRequest<OpenAIHealthFlag>('/health/openai', { unauthenticated: true }),
    staleTime: 30_000,
    refetchInterval: 30_000,
  });

  if (!user) return null;
  if (data?.configured !== false) return null;

  const isAdmin = user.role === 'admin';

  return (
    <div className="border-b border-caution/30 bg-caution/5 px-4 py-2.5 text-sm">
      <div className="mx-auto flex max-w-6xl items-center gap-3">
        <AlertTriangle className="size-4 shrink-0 text-caution" />
        <div className="flex-1">
          <p className="font-medium text-ink">OpenAI is not configured.</p>
          <p className="text-xs text-ink-muted">
            {isAdmin
              ? 'Agent chats and voice transcription are disabled until an API key is set.'
              : 'Agent chats and voice transcription are temporarily unavailable. Your administrator has been notified.'}
          </p>
        </div>
        {isAdmin && (
          <Link
            to="/admin/settings"
            className={
              'inline-flex items-center gap-1.5 rounded-tight bg-caution px-3 py-1.5 text-xs font-medium text-paper ' +
              'transition-colors duration-instant ease-standard hover:brightness-95'
            }
          >
            <SettingsIcon className="size-3.5" />
            Open settings
          </Link>
        )}
      </div>
    </div>
  );
}
